import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PlugsConnected, Plugs, CheckCircle, Info } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PROVIDERS = [
  { id: "brightlocal", name: "BrightLocal", hint: "Cheaper, pay-per-citation. You keep the listings. Get your key in BrightLocal → Account → API." },
  { id: "yext", name: "Yext", hint: "Premium real-time sync (~$449+/yr per location). Get your key in Yext → Account Settings → App Directory / API." },
];

export default function ConnectAggregator() {
  const [state, setState] = useState(null);
  const [provider, setProvider] = useState("brightlocal");
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/integrations");
      setState(data);
      if (data.provider) setProvider(data.provider);
    } catch { setState({ connected: false }); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!apiKey.trim()) { toast.error("Enter an API key"); return; }
    setBusy(true);
    try {
      await api.post("/admin/integrations", { provider, api_key: apiKey.trim() });
      toast.success("Aggregator connected");
      setApiKey("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/integrations");
      toast.success("Disconnected");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const connected = state?.connected;
  const activeHint = PROVIDERS.find((p) => p.id === provider)?.hint;

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="connect-aggregator-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#0055FF]">
          <PlugsConnected weight="duotone" size={20} />
          <p className="text-xs font-bold uppercase tracking-[0.14em]">Connect Aggregator</p>
        </div>
        <span data-testid="aggregator-status" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${connected ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-500" : "bg-slate-400"}`} />
          {connected ? "Connected" : "Not connected"}
        </span>
      </div>
      <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-2">Power Auto-Sync submissions</h2>
      <p className="text-sm text-slate-600 mt-1.5">
        Add a BrightLocal or Yext API key to enable real listing pushes for your Auto-Sync customers. Until a key is added, Auto-Sync businesses are queued for manual review.
      </p>

      {connected ? (
        <div className="mt-6 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <CheckCircle weight="fill" size={20} className="text-emerald-600" />
            <div>
              <p className="text-sm font-semibold text-slate-900 capitalize">{state.provider}</p>
              <p className="text-xs text-slate-500 font-mono">{state.masked_key}</p>
            </div>
          </div>
          <Button variant="secondary" disabled={busy} onClick={disconnect} data-testid="aggregator-disconnect"
            className="bg-white border border-slate-200 text-red-600 hover:bg-red-50">
            <Plugs size={16} className="mr-1.5" /> Disconnect
          </Button>
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-[200px_1fr_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">Provider</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger data-testid="aggregator-provider" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">API key</Label>
            <Input type="password" data-testid="aggregator-api-key" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder="Paste your API key" className="h-10" />
          </div>
          <Button onClick={save} disabled={busy} data-testid="aggregator-save"
            className="h-10 bg-[#0055FF] hover:bg-[#0044CC] text-white">Connect</Button>
        </div>
      )}

      <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 p-3 flex gap-2.5 text-xs text-slate-600">
        <Info size={16} weight="fill" className="shrink-0 mt-0.5 text-slate-400" />
        <p>{activeHint} Your key is stored securely and never shown in full.</p>
      </div>
    </div>
  );
}
